// @ts-check
/**
 * @typedef {Object} ffSliderCustomProperties
 * @property {Function} ffRestrains Removes empty spaces generated for the arrows and dots if they aren't being displayed.
 * @property {Function} ffResizeSlides
 * @property {() => Promise<void>} ffInit
 * @property {{
 *   update: Function,
 *   destroy: Function,
 *   updateSlides: Function,
 *   appendSlide: Function,
 *   removeAllSlides: Function,
 *   removeSlide: Function,
 *   on: (
 *     eventName:
 *       | "activeIndexChange"
 *       | "afterInit"
 *       | "beforeDestroy"
 *       | "beforeInit"
 *       | "beforeLoopFix"
 *       | "beforeResize"
 *       | "beforeSlideChangeStart"
 *       | "beforeTransitionStart"
 *       | "breakpoint"
 *       | "changeDirection"
 *       | "click"
 *       | "destroy"
 *       | "doubleClick"
 *       | "doubleTap"
 *       | "fromEdge"
 *       | "init"
 *       | "lock"
 *       | "loopFix"
 *       | "momentumBounce"
 *       | "observerUpdate"
 *       | "orientationchange"
 *       | "progress"
 *       | "reachBeginning"
 *       | "reachEnd"
 *       | "realIndexChange"
 *       | "resize"
 *       | "setTransition"
 *       | "setTranslate"
 *       | "slideChange"
 *       | "slideChangeTransitionEnd"
 *       | "slideChangeTransitionStart"
 *       | "slideNextTransitionEnd"
 *       | "slideNextTransitionStart"
 *       | "slidePrevTransitionEnd"
 *       | "slidePrevTransitionStart"
 *       | "slideResetTransitionEnd"
 *       | "slideResetTransitionStart"
 *       | "sliderFirstMove"
 *       | "sliderMove"
 *       | "slidesGridLengthChange"
 *       | "slidesLengthChange"
 *       | "slidesUpdated"
 *       | "snapGridLengthChange"
 *       | "snapIndexChange"
 *       | "tap"
 *       | "toEdge"
 *       | "touchEnd"
 *       | "touchMove"
 *       | "touchMoveOpposite"
 *       | "touchStart"
 *       | "transitionEnd"
 *       | "transitionStart"
 *       | "unlock"
 *       | "update",
 *     callback: Function
 *   ) => void
 * }} ffSwiper
 *
 * @typedef {ffSliderCustomProperties & HTMLElement} FfSlider
 */

if (!customElements.get("ff-slider")) {
  class FfSlider extends HTMLElement {
    constructor() {
      super();

      this.ffSwiperSelector = this.dataset.ffSwiperSelector;

      /**
       * @type {HTMLElement}
       */
      this.ffSlider = (() => {
        const ffSlider = this.querySelector(this.ffSwiperSelector || "");
        if (ffSlider) return ffSlider;
        throw new Error(
          `There wasn't found an element with the selector: '${this.ffSwiperSelector}, which is needed for the creation of the swiper instance.`
        );
      })();

      this.ffSliderType = this.dataset.ffSliderType;
      
      // Si es thumbs-slider-main, buscar el thumbs-slider-thumbs relacionado
      if (this.ffSliderType === "thumbs-slider-main") {
        this.ffThumbsSlider = this.findRelatedSlider("thumbs-slider-thumbs");
      }
      
      this.ffInit();
    }

    /**
     * Busca un slider relacionado en el DOM
     * @param {string} sliderType - Tipo de slider a buscar
     * @returns {HTMLElement | null}
     */
    findRelatedSlider(sliderType) {
      // Intentar por data-ff-thumbs-query si existe
      if (this.dataset.ffThumbsQuery) {
        const slider = document.querySelector(
          `ff-slider[data-ff-slider-type="${sliderType}"][data-ff-thumbs-query="${this.dataset.ffThumbsQuery}"]`
        );
        if (slider) return slider;
      }
      
      // Buscar en niveles superiores del DOM (hasta 5 niveles)
      let container = this.parentElement;
      for (let level = 0; level < 5 && container; level++) {
        const slider = container.querySelector(`ff-slider[data-ff-slider-type="${sliderType}"]`);
        if (slider) return slider;
        container = container.parentElement;
      }
      
      // Último recurso: buscar en toda la página
      return document.querySelector(`ff-slider[data-ff-slider-type="${sliderType}"]`);
    }

    ffInit = () => {
      this.ffInitBaseDinamic();
    };

    /**
     * This case was created for the cart drawer, which was fetching the slider html, so the configuration script was not being executed.
     * @param {Boolean} ffScriptAppended 
     */
    ffInitBaseDinamic = (ffScriptAppended = false) => {
      const params = ffSwiperConfigurations[this.id];
      if (!params) return;

      if (this.ffSliderType === "slider") {
        this.ffSwiper = new Swiper(this.ffSwiperSelector, params);
      }
      else if (this.ffSliderType === "thumbs-slider-main") {
        this.initThumbsSliderMain(params);
      }
      else if (this.ffSliderType === "thumbs-slider-thumbs") {
        this.initThumbsSliderThumbs(params);
      }
      
      this.classList.remove("ff-slider-uninitialized");
    }

    initThumbsSliderMain(params) {
      if (this.ffThumbsSlider?.ffSwiper) {
        // Si el thumbs slider ya está inicializado, usarlo directamente
        params.thumbs = { swiper: this.ffThumbsSlider.ffSwiper };
        this.ffSwiper = new Swiper(this.ffSwiperSelector, params);
      } else {
        // Si no está inicializado, esperar el evento
        this.addEventListener("ff:thumbs-slider-thumbs:init", (e) => {
          params.thumbs = { swiper: e.detail.ffSwiper };
          this.ffSwiper = new Swiper(this.ffSwiperSelector, params);
        }, { once: true });
      }
    }

    initThumbsSliderThumbs(params) {
      this.ffSwiper = new Swiper(this.ffSwiperSelector, params);
      
      const mainSlider = this.findRelatedSlider("thumbs-slider-main");
      const event = new CustomEvent('ff:thumbs-slider-thumbs:init', {
        bubbles: !mainSlider, // Solo burbujear si no se encontró el main slider
        detail: { ffSwiper: this.ffSwiper }
      });
      
      (mainSlider || this).dispatchEvent(event);
    }
  }

  customElements.define("ff-slider", FfSlider);
}